import React, { useCallback, useEffect, useState } from 'react';
import { Placement as PopperPlacement } from '@popperjs/core';
import './styles.css';
import Popper from './components/Popper';
import { useOverlayState, useOverlayDispatch } from './OverlayContext';
import { useCssHandles } from '../../utils/functions';

type TransitionComponentType = 'fade';

interface Offsets {
	distance: number;
	skidding: number;
}

interface Classes {
	arrow: string;
	popper: string;
	backdrop: string;
	container: string;
	popperArrow: string;
	backdropContainer: string;
	outsideClickHandler: string;
}

interface Props extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
	showArrow?: boolean;
	children: React.ReactNode;
	classes?: Partial<Classes>;
	offsets?: Partial<Offsets>;
	placement?: PopperPlacement;
	backdrop?: 'visible' | 'none';
	scrollBehavior?: 'lock-page-scroll' | 'close-on-scroll' | 'default';
	transitionComponent?: TransitionComponentType;
	blockClass?: string;
	componentId?: string;
}

const CSS_HANDLES = [
	'arrow',
	'popper',
	'backdrop',
	'container',
	'popperArrow',
	'backdropContainer',
	'outsideClickHandler',
] as const;

export default function BaseOverlay(props: Props) {
	const {
		componentId,
		ref,
		role,
		offsets,
		onClick,
		children,
		backdrop = 'none',
		showArrow = false,
		placement = 'bottom',
		className: classNameProp,
		scrollBehavior = 'default',
		...rest
	} = props;
	const { open, containerRef, triggerMode } = useOverlayState();
	const dispatch = useOverlayDispatch();
	const { handles }: any = useCssHandles('vtex-overlay-layout-0-x', CSS_HANDLES, props.blockClass);
	const [arrowRef, setArrowRef] = useState<HTMLSpanElement | null>(null);

	const handleKeydown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key !== 'Escape' || triggerMode !== 'click') {
				return;
			}
			dispatch({ type: 'CLOSE_OVERLAY' });
		},
		[dispatch, triggerMode],
	);

	const closeOnScroll = (e: any) => {
		e.preventDefault();
		dispatch({ type: 'CLOSE_OVERLAY' });
	};

	useEffect(() => {
		if (open) {
			if (scrollBehavior === 'close-on-scroll') {
				window.addEventListener('scroll', closeOnScroll);
			}
		}

		return () => {
			window.removeEventListener('scroll', closeOnScroll);
		};
	}, [closeOnScroll, scrollBehavior, open]);

	const handleClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
		(e) => {
			if (triggerMode === 'click') {
				// The Trigger works as a toggle, so if this event
				// go up to the Trigger it will close the Overlay
				e.stopPropagation();
			}

			if (onClick) {
				onClick(e);
			}
		},
		[onClick, triggerMode],
	);

	const popperClasses = {
		container: 'classes.popper',
		backdrop: 'classes.backdrop',
		popperArrow: 'classes.popperArrow',
		backdropContainer: 'classes.backdropContainer',
	};

	const arrowClasses = 'arrowClasses';

	return (
		<Popper
			componentId={componentId}
			blockClass={props.blockClass}
			transition
			open={open}
			offsets={offsets}
			arrowEl={arrowRef}
			placement={placement}
			classes={popperClasses}
			anchorEl={containerRef?.current}
			backdrop={backdrop !== 'none' ? 'clickable' : 'none'}
		>
			<div className={handles.outsideClickHandler}>
				<div
					role={role}
					tabIndex={-1}
					onClick={handleClick}
					onKeyDown={handleKeydown}
					className={`${handles.container} outline-0 pv2 bg-base br bl bb bt b--muted-4 br2 w-100`}
					{...rest}
				>
					{showArrow && <span className={arrowClasses} ref={setArrowRef} />}
					{children}
				</div>
			</div>
		</Popper>
	);
}
